import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup
} from '@mui/material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useOrgCuratedCurrencies } from '../hooks/useOrgCuratedCurrencies';
import { formatCurrencyPickerLabel } from '../utils/iso4217';

interface AddAccountFormData {
  name: string;
  description: string;
  accountNumber: string;
  billingRate: string;
  currency: string;
  defaultTasks: string[];
}

export default function AddAccount() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('companyId');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyDefaultTasks, setCompanyDefaultTasks] = useState<string[]>([]);
  const { curatedCodes } = useOrgCuratedCurrencies(organizationId);
  const [formData, setFormData] = useState<AddAccountFormData>({
    name: '',
    description: '',
    accountNumber: '',
    billingRate: '',
    currency: 'EUR',
    defaultTasks: []
  });

  useEffect(() => {
    if (!organizationId) {
      setError('Organization ID is missing.');
    }
    if (!companyId) {
      setError('Company ID is missing.');
    }
    // Load company default tasks
    loadCompanyDefaultTasks();
  }, [organizationId, companyId]);

  useEffect(() => {
    if (curatedCodes.length === 0) return;
    setFormData((prev) => {
      if (curatedCodes.includes(prev.currency)) return prev;
      return { ...prev, currency: curatedCodes[0] };
    });
  }, [curatedCodes]);

  const loadCompanyDefaultTasks = async () => {
    if (!companyId) return;
    
    try {
      const response = await api.getCompany(companyId);
      if (response.success && response.data) {
        const company = response.data as { defaultTasks?: string[]; currency?: string };
        // Get company default tasks (inherited from organization + company-specific)
        const tasks = company.defaultTasks || [];
        setCompanyDefaultTasks(tasks);
        // Pre-select all default tasks; currency defaults from company (§1.1)
        setFormData((prev) => ({
          ...prev,
          defaultTasks: tasks,
          currency: company.currency || prev.currency
        }));
      }
    } catch (err: any) {
      console.error('Error loading company default tasks:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!organizationId) {
      setError('Organization ID is missing.');
      return;
    }

    if (!companyId) {
      setError('Company ID is missing.');
      return;
    }

    // Validate account name
    if (!formData.name || !formData.name.trim()) {
      setError('Please enter an account name.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const accountData = {
        organizationId,
        companyId,
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        accountNumber: formData.accountNumber.trim() || null,
        billingRate: formData.billingRate ? parseFloat(formData.billingRate) : null,
        currency: formData.currency || curatedCodes[0] || 'EUR',
        isActive: true,
        defaultTasks: formData.defaultTasks
      };

      const response = await api.createAccount(accountData);
      
      if (response.success) {
        // Navigate back to company view in manage
        navigate(`/org/${organizationId}/manage`, {
          state: {
            message: 'Account created successfully',
            context: { type: 'company', id: companyId }
          }
        });
      } else {
        setError(response.message || 'Failed to create account');
      }
    } catch (err: any) {
      console.error('Error creating account:', err);
      setError(err.response?.data?.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/org/${organizationId}/manage`);
  };

  const handleTaskToggle = (taskName: string) => {
    setFormData(prev => {
      const currentTasks = prev.defaultTasks;
      if (currentTasks.includes(taskName)) {
        return { ...prev, defaultTasks: currentTasks.filter(t => t !== taskName) };
      } else {
        return { ...prev, defaultTasks: [...currentTasks, taskName] };
      }
    });
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          Add New Account
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Account Name */}
            <Grid item xs={12}>
              <TextField
                label="Account Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
                autoFocus
                disabled={loading}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={4}
                disabled={loading}
                helperText="Optional."
              />
            </Grid>

            {/* Account Number */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Account Number"
                value={formData.accountNumber}
                onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                fullWidth
                disabled={loading}
                helperText="Optional."
              />
            </Grid>

            {/* Billing Rate */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Billing Rate"
                type="number"
                value={formData.billingRate}
                onChange={(e) => setFormData({ ...formData, billingRate: e.target.value })}
                fullWidth
                disabled={loading}
                helperText="Optional. Hourly rate."
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>{formData.currency}</Typography>
                }}
              />
            </Grid>

            {/* Currency */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  label="Currency"
                  disabled={loading || curatedCodes.length === 0}
                >
                  {curatedCodes.map((code) => (
                    <MenuItem key={code} value={code}>
                      {formatCurrencyPickerLabel(code)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Default Tasks */}
            {companyDefaultTasks.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Default Tasks
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select which default tasks to create for this account:
                </Typography>
                <FormGroup>
                  {companyDefaultTasks.map((task) => (
                    <FormControlLabel
                      key={task}
                      control={
                        <Checkbox
                          checked={formData.defaultTasks.includes(task)}
                          onChange={() => handleTaskToggle(task)}
                          disabled={loading}
                        />
                      }
                      label={task}
                    />
                  ))}
                </FormGroup>
              </Grid>
            )}

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={loading}
                  sx={{ minWidth: 80, height: 32 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || !formData.name.trim()}
                  sx={{ minWidth: 120, height: 32 }}
                >
                  Create Account
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}
